import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAllPaabudIndberetninger } from "@/api/paabud";
import { getAllPaalaegIndberetninger } from "@/api/paalaeg";
import type { AfgorelseOverviewModel } from "@/interfaces/afgorelse";

export const AFGOERELSER_QUERY_KEY = ["cle-compliance", "afgorelser"] as const;

const fetchAllAfgorelser = async (): Promise<AfgorelseOverviewModel[]> => {
  const [paabud, paalaeg] = await Promise.all([
    getAllPaabudIndberetninger(),
    getAllPaalaegIndberetninger(),
  ]);

  return [
    ...paabud.map((item): AfgorelseOverviewModel => ({
      ...item,
      afgorelseType: "paabud",
    })),
    ...paalaeg.map((item): AfgorelseOverviewModel => ({
      ...item,
      afgorelseType: "paalaeg",
    })),
  ];
};

export const useAfgorelser = () => {
  const query = useQuery<AfgorelseOverviewModel[]>({
    queryKey: AFGOERELSER_QUERY_KEY,
    queryFn: fetchAllAfgorelser,
    staleTime: 5 * 60 * 1000,
  });

  const sortedData = useMemo(() => {
    if (!query.data) {
      return undefined;
    }

    return [...query.data].sort((left, right) => {
      // Sager i bero har ingen frist. De skal ligge sidst, ikke først: en manglende
      // frist betyder, at der ikke er noget at gøre lige nu.
      const leftDate = left.frist
        ? new Date(left.frist).getTime()
        : Number.POSITIVE_INFINITY;
      const rightDate = right.frist
        ? new Date(right.frist).getTime()
        : Number.POSITIVE_INFINITY;

      return leftDate - rightDate;
    });
  }, [query.data]);

  return {
    ...query,
    data: sortedData,
  };
};
